// src/components/CustomerAuth/CustomerSignupForm.tsx
import React, { useState } from 'react';
import Lottie from 'lottie-react';
import loaderAnimation from '../../loader.json';
import './CustomerLogin.css';

interface SignupData {
  companyName: string;
  companyRegNo: string;
  address1: string;
  address2: string;
  town: string;
  postcode: string;
  email: string;
  phone: string;
  brandsInterested: string[];
}

const brands = [
  { id: 'blomus', name: 'Blomus', logo: '/logos/blomus.png' },
  { id: 'elvang', name: 'Elvang', logo: '/logos/elvang.png' },
  { id: 'myflame', name: 'My Flame Lifestyle', logo: '/logos/myflame.png' },
  { id: 'rader', name: 'Räder', logo: '/logos/rader.png' },
  { id: 'remember', name: 'Remember', logo: '/logos/remember.png' },
  { id: 'relaxound', name: 'Relaxound', logo: '/logos/relaxound.png' },
  { id: 'gefu', name: 'GEFU', logo: '/logos/gefu.png' },
  { id: 'ppd', name: 'PPD', logo: '/logos/ppd.png' }
];

export default function CustomerSignupForm() {
  const [formData, setFormData] = useState<SignupData>({
    companyName: '',
    companyRegNo: '',
    address1: '',
    address2: '',
    town: '',
    postcode: '',
    email: '',
    phone: '',
    brandsInterested: []
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBrandToggle = (brandId: string) => {
    setFormData(prev => ({
      ...prev,
      brandsInterested: prev.brandsInterested.includes(brandId)
        ? prev.brandsInterested.filter(id => id !== brandId)
        : [...prev.brandsInterested, brandId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      // Send email to sales@dmbrands.co.uk
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://dm-portal.onrender.com'}/api/signup-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          brandNames: formData.brandsInterested.map(id => 
            brands.find(brand => brand.id === id)?.name || id
          )
        }),
      });

      if (response.ok) {
        setMessage({
          type: 'success',
          text: 'Thank you for your interest! We\'ll be in touch shortly.'
        });
        // Reset form
        setFormData({
          companyName: '',
          companyRegNo: '',
          address1: '',
          address2: '',
          town: '',
          postcode: '',
          email: '',
          phone: '',
          brandsInterested: []
        });
      } else {
        throw new Error('Failed to submit inquiry');
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Sorry, there was an error submitting your inquiry. Please try again or contact us directly.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="customer-login-page">
      <div className="gradient-overlay"></div>
      <div className="floating-accent"></div>
      
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="logo-container">
              <img 
                src="/logos/dmb-logo.png" 
                alt="DM Brands" 
                className="logo-image"
              />
            </div>
            <div className="auth-hero">
              <h1>DM Brands</h1>
            </div>
            <h2 className="login-subtitle">Request Account Access</h2>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {message && (
              <div className={`${message.type === 'error' ? 'error-message' : 'success-message'}`}>
                <div className="message-icon">
                  {message.type === 'error' ? '⚠️' : '✅'}
                </div>
                <span>{message.text}</span>
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="companyName" className="form-label">Company Name *</label>
              <input
                id="companyName"
                name="companyName"
                type="text"
                placeholder="Enter company name"
                value={formData.companyName}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="companyRegNo" className="form-label">Company Registration No.</label>
              <input
                id="companyRegNo"
                name="companyRegNo"
                type="text"
                placeholder="Optional"
                value={formData.companyRegNo}
                onChange={handleInputChange}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="address1" className="form-label">Address Line 1 *</label>
              <input
                id="address1"
                name="address1"
                type="text"
                placeholder="Enter address"
                value={formData.address1}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="address2" className="form-label">Address Line 2</label>
              <input
                id="address2"
                name="address2"
                type="text"
                placeholder="Optional"
                value={formData.address2}
                onChange={handleInputChange}
                className="form-input"
                disabled={isLoading}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="town" className="form-label">Town/City *</label>
                <input
                  id="town"
                  name="town"
                  type="text"
                  placeholder="Enter town/city"
                  value={formData.town}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="postcode" className="form-label">Postcode *</label>
                <input
                  id="postcode"
                  name="postcode"
                  type="text"
                  placeholder="Enter postcode"
                  value={formData.postcode}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="phone" className="form-label">Phone Number *</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Brands You're Interested In</label>
              <div className="brands-selection">
                {brands.map(brand => (
                  <div 
                    key={brand.id}
                    className={`brand-option ${formData.brandsInterested.includes(brand.id) ? 'selected' : ''}`}
                    onClick={() => handleBrandToggle(brand.id)}
                  >
                    <img src={brand.logo} alt={brand.name} className="brand-logo" />
                    <span className="brand-name">{brand.name}</span>
                    <div className="brand-checkmark">✓</div>
                  </div>
                ))}
              </div>
            </div>

            <button 
              type="submit" 
              className="login-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="button-loader">
                    <Lottie 
                      animationData={loaderAnimation}
                      loop={true}
                      autoplay={true}
                      style={{ 
                        width: '24px', 
                        height: '24px',
                        filter: 'brightness(0) invert(1)'
                      }}
                    />
                  </div>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <span>Submit Inquiry</span>
                  <svg className="button-arrow" viewBox="0 0 24 24" fill="white">
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          <div className="login-footer">
            <div className="help-text">
              <p>Already have an account?</p>
            </div>
            
            <div className="signup-section">
              <button 
                type="button" 
                className="signup-link"
                onClick={() => window.dispatchEvent(new CustomEvent('showLogin'))}
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}